import type { AdminFeatureSwitch } from '@steado/admin-contract/feature-switches';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Panel } from '@/components/panel/panels';
import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { TONES } from '@/components/table/tone';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import { featureSwitchesQueryOptions, setFeatureSwitch } from './feature-switches-query';

/**
 * Whole features an admin turns on and off without a deploy: Steado for Gyms
 * and the monthly Recap. Until an admin sets one it follows its server
 * setting, which is shown beside it.
 */
export function FeatureSwitchesScreen() {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const switches = useQuery(featureSwitchesQueryOptions(api));
  const [pending, setPending] = useState<AdminFeatureSwitch | null>(null);

  const mutation = useMutation({
    mutationFn: (item: AdminFeatureSwitch) => setFeatureSwitch(api, item, !item.enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feature-switches'] });
      setPending(null);
    },
    retry: false,
  });

  return (
    <div className="flex max-w-[960px] flex-col gap-3">
      <Panel
        title="Feature switches"
        description="Takes effect in the API at once and stays until an admin changes it. Each change is recorded in Admin activity."
        bleed
      >
        {switches.isError ? (
          <div className="border-t border-border px-[18px] py-3.5">
            <Alert role="alert" tone="err">
              <AlertDescription>{switches.error.message}</AlertDescription>
            </Alert>
          </div>
        ) : null}
        <ul>
          {(switches.data ?? []).map((item) => (
            <li key={item.key} className="flex items-start gap-3 border-t border-border px-[18px] py-3">
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-sm font-semibold text-foreground">{item.label}</span>
                <span className="text-xs text-pretty text-muted-foreground">{item.description}</span>
                <span className="font-mono text-[11.5px] text-muted-foreground">
                  {item.source === 'default'
                    ? `${item.key} · following the server setting (${item.defaultEnabled ? 'on' : 'off'})`
                    : `${item.key} · set by an admin${item.updatedAt === null ? '' : ` ${formatDateTime(item.updatedAt)}`} · server setting ${item.defaultEnabled ? 'on' : 'off'}`}
                </span>
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-2 pt-0.5">
                <span
                  className={cn(
                    'inline-flex h-5 items-center rounded-[10px] px-2 text-xs font-semibold',
                    TONES[item.enabled ? 'ok' : 'err'],
                  )}
                >
                  {item.enabled ? 'on' : 'off'}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={!can.mutate}
                  title={can.mutate ? undefined : 'Requires the admin role.'}
                  onClick={() => {
                    setPending(item);
                  }}
                >
                  {item.enabled ? 'Turn off…' : 'Turn on…'}
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </Panel>

      {pending === null ? null : (
        <ConfirmDialog
          title={`Turn ${pending.label} ${pending.enabled ? 'off' : 'on'}?`}
          body={`${pending.description} This takes effect at once and is recorded against your account.`}
          confirmLabel={pending.enabled ? 'Turn off' : 'Turn on'}
          destructive={pending.enabled}
          typeToConfirm={pending.key}
          isPending={mutation.isPending}
          {...(mutation.isError ? { error: mutation.error.message } : {})}
          onConfirm={() => {
            mutation.mutate(pending);
          }}
          onCancel={() => {
            setPending(null);
          }}
        />
      )}
    </div>
  );
}
